import Box from '@mui/material/Box';
import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize.tsx';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import type { Account } from '@openx/types/account.ts';
import { type FieldArrayWithId, type Path, type UseFieldArrayAppend, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { AudienceShares } from '../../types.ts';
import { createDefaultShare } from '../../utils/createDefaultShare.ts';

const EMPTY: string[] = [];

type SelectedPublishersProps = {
  field: FieldArrayWithId<AudienceShares, 'audience_share', 'id'>;
  itemIndex: number;
  recipientMap: Record<string, Account>;
  options: string[];
  totalItems: number;
  handleDelete: (index: number) => void;
  append: UseFieldArrayAppend<AudienceShares, 'audience_share'>;
};

export const SelectedPublishers = ({
  recipientMap,
  itemIndex,
  field,
  options,
  totalItems,
  handleDelete,
  append,
}: SelectedPublishersProps) => {
  const { t } = useTranslation();
  const {
    setValue,
    formState: { isSubmitting },
  } = useFormContext<AudienceShares>();

  const publisherDropdownName = `audience_share.${itemIndex}.recipient_account_id` satisfies Path<AudienceShares>;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          flex: 1,
        }}
      >
        <RHFAutoCompleteVirtualize
          key={field.id}
          name={publisherDropdownName}
          options={options ?? EMPTY}
          data-test="publisher-dropdown"
          getOptionLabel={option => recipientMap[option]?.name ?? option}
          textFieldProps={{
            label: t('Select publisher(s) to share audience with'),
            placeholder: t('[ Select Publisher ]'),
            margin: 'dense',
          }}
          onChange={(_, value, reason) => {
            if (value.length === 0 && totalItems > 1) {
              handleDelete(itemIndex);
            } else {
              setValue(publisherDropdownName, value, { shouldDirty: true, shouldTouch: true });

              if (reason === 'selectOption' && value.length === 1) {
                append(createDefaultShare(field.audience_external_openaudience_id));
              }
            }
          }}
          disabled={isSubmitting}
          multiple
          limitTags={10}
        />
      </Box>

      <Button
        variant="text"
        color="error"
        onClick={() => handleDelete(itemIndex)}
        sx={{
          visibility: field.recipient_account_id.length ? 'visible' : 'hidden',
          mt: 2.5,
        }}
        aria-hidden={!field.recipient_account_id.length}
        tabIndex={field.recipient_account_id.length ? 0 : -1}
      >
        {t('Remove')}
      </Button>
    </Box>
  );
};
